import * as io from 'socket.io-client';

/**
 * Configuration options
 */
export interface IConfiguration {
  /**
   * URL of Results-Hub server to connect to
   */
  serverUrl: string;
  options?: SocketIOClient.ConnectOpts;
}

/**
 * Message format for subscribing to Results Hub
 * @todo Move this to Results Hub itself
 */
export interface ISubscribeMessage {
  /**
   * URL requested
   */
  url: string;
  /**
   * Interval to request at
   * Note: this is more of a suggestion. If another existing topic exists,
   * it will use the existing interval
   */
  interval: number;
}

export type Callback = (x?: any) => any;

/**
 * Representation of a stored subscription
 */
export interface ISubscription extends ISubscribeMessage {
  /**
   * Callback to execute when an update occurs
   */
  callback: Callback;
}

let client: any = null;

const configuration: IConfiguration = {
  options: {transports: ['websocket']},
  serverUrl: '',
};

const currentSubscriptions: {[key: string]: ISubscription[]} = {};

/**
 * Sets the global configuration for the Results Hub connection
 * @param serverUrl URL of server to connect to
 * @throws {Error} Illegal Operation if configuration is attempted while connect exists
 */
export function configure(serverUrl: string, options?: SocketIOClient.ConnectOpts): IConfiguration {
  if (client != null) {
    throw new Error('Illegal Operation');
  }
  configuration.serverUrl = serverUrl;
  if (options) {
    configuration.options = options;
  }
  return {...configuration};
}

/**
 * Initializes the global Results Hub connection, if necessary
 */
function initialize(): Promise<void> {
  return new Promise((resolve) => {
    if (client === null) {
      if (!configuration.serverUrl) {
        throw new Error('Missing Server URL');
      }
      client = io(configuration.serverUrl, configuration.options);
      client.on('connect', () => {
        // On reconnect, restore expected/existing subscriptions
        // `on` handler already established.
        Object.keys(currentSubscriptions).forEach((x) => {
          const subs = currentSubscriptions[x];
          subs.forEach((sub) => {
            client.emit('subscribe', sub);
          });
        });
        resolve();
      });
    } else {
      resolve();
    }
  });
}

export function maybeSubscription(url = '', callback: Callback): ISubscription | undefined {
  return (currentSubscriptions[url] || [])
    .filter((x) => x.callback === callback)
    .map((x) => ({...x}))
    .find((x) => !!x);
}

/**
 * Creates/Joins a topic for the requested URL
 *
 * @param url URL being requested
 * @param callback Function executed on topic update
 * @param interval Requested interval to poll at. If an existing topic exists, the current
 * interval will be used
 */
export async function subscribe(url = '', callback: Callback, interval = 30000) {
  try {
    await initialize();
  } catch (e) {
    throw e;
  }

  let existingTopic = maybeSubscription(url, callback);

  client.on(url, callback);

  if (!existingTopic) {
    const subMsg: ISubscribeMessage = {interval, url};
    const sub: ISubscription = {callback, ...subMsg};

    currentSubscriptions[url] = (currentSubscriptions[url] || []).concat(sub);
    if (!existingTopic) {
      client.emit('subscribe', subMsg);
    }
    existingTopic = {...sub};
  }
  return existingTopic;
}

/**
 * Unsubscribes from a topic with a specific callback
 * @param url URL used during subscription
 * @param callback Callback specified during subscription
 */
export function unsubscribe(url = '', callback: Callback) {
  if (client !== null) {
    if (url in currentSubscriptions) {
      const sub = currentSubscriptions[url].find((x) => x.callback === callback);
      if (sub) {
        const subMsg = {interval: 0, url};
        client.removeListener(url, sub.callback);
        if (currentSubscriptions[url].length > 1) {
          currentSubscriptions[url] = currentSubscriptions[url]
            .filter((x) => x.callback !== callback);
        } else {
          client.emit('unsubscribe', subMsg);
          delete currentSubscriptions[url];
          if (Object.keys(currentSubscriptions).length === 0) {
            dispose();
          }
        }
        return true;
      }
    }
  }
  return false;
}

/**
 * Unsubscribes all callbacks for a given URL
 * @param url URL of subscription
 */
export function unsubscribeUrl(url = '') {
  return (currentSubscriptions[url] || [])
    .reduce((acc, cur) => unsubscribe(url, cur.callback) ? acc + 1 : acc, 0);
}

/**
 * Unsubscribes all registered subscriptions
 */
export function unsubscribeAll() {
  return Object.keys(currentSubscriptions)
    .reduce((acc, cur): number => acc + unsubscribeUrl(cur), 0);
}

/**
 * Kills everything here
 */
export function dispose() {
  const unsubbed = unsubscribeAll();
  if (client) {
    client.disconnect();
  }
  client = null;
  return unsubbed;
}
